import { cn } from '../../../lib/utils'
import type { UseCase } from './use-case-step'

type Frequency = 'daily' | 'weekdays' | 'weekly' | 'biweekly'

const FREQUENCIES: { value: Frequency; label: string; description: string }[] = [
  { value: 'daily', label: 'Daily', description: 'A fresh episode every day' },
  { value: 'weekdays', label: 'Weekdays', description: 'Monday to Friday only' },
  { value: 'weekly', label: 'Weekly', description: 'Once a week, every Monday' },
  { value: 'biweekly', label: 'Biweekly', description: 'Every two weeks' },
]

const DURATIONS = [10, 20, 30]

// Contextual hints for each use case
const USE_CASE_CONFIG: Record<UseCase, { heading: string; subheading: string; frequencyHint: string; durationHint: string; timeHint: string }> = {
  'busy-professional': {
    heading: 'When do you want it ready?',
    subheading: 'We will generate your episode and have it ready for your commute or workout.',
    frequencyHint: 'Weekdays are great for staying current',
    durationHint: '10 min is perfect for a commute',
    timeHint: 'Set it to run before you wake up',
  },
  'student-learner': {
    heading: 'Set your study rhythm',
    subheading: 'Consistency helps with retention.',
    frequencyHint: 'Weekdays match most course schedules',
    durationHint: '15-20 min is ideal for a focused session',
    timeHint: 'Morning delivery gives you flexibility',
  },
  'curious-explorer': {
    heading: 'Plan your discovery time',
    subheading: 'Whenever you want to learn something new.',
    frequencyHint: 'Weekly gives you time to absorb',
    durationHint: 'Longer episodes let topics breathe',
    timeHint: 'Weekend mornings are perfect',
  },
}

interface ScheduleStepProps {
  frequency: Frequency
  episodeDurationMinutes: number
  deliveryTimeLocal: number
  timezone: string
  useCase: UseCase | null
  maxDurationMinutes?: number
  onFrequencyChange: (value: Frequency) => void
  onDurationChange: (value: number) => void
  onDeliveryTimeChange: (value: number) => void
  onTimezoneChange: (value: string) => void
}

export function ScheduleStep({
  frequency,
  episodeDurationMinutes,
  deliveryTimeLocal,
  timezone,
  useCase,
  maxDurationMinutes,
  onFrequencyChange,
  onDurationChange,
  onDeliveryTimeChange,
  onTimezoneChange,
}: ScheduleStepProps) {
  const hours = Math.floor(deliveryTimeLocal / 60)
  const minutes = deliveryTimeLocal % 60
  const timeValue = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const [h, m] = e.target.value.split(':').map(Number)
    onDeliveryTimeChange(h * 60 + m)
  }

  const config = useCase ? USE_CASE_CONFIG[useCase] : null

  return (
    <div>
      <h2 className="mb-2 text-3xl font-extrabold tracking-tight text-foreground">
        {config?.heading || 'How often and how long?'}
      </h2>
      <p className="mb-8 text-base font-medium text-foreground/60">
        {config?.subheading || 'Set your preferred schedule and episode length.'}
      </p>

      {/* Frequency */}
      <div className="mb-8">
        <label className="mb-3 block text-[15px] font-bold text-foreground">Frequency</label>
        <div className="grid grid-cols-2 gap-3">
          {FREQUENCIES.map((freq) => (
            <button
              key={freq.value}
              onClick={() => onFrequencyChange(freq.value)}
              className={cn(
                'group cursor-pointer rounded-[20px] border p-4 text-left transition-all',
                frequency === freq.value
                  ? 'border-brand-300 bg-brand-50/70 ring-1 ring-brand-300'
                  : 'border-black/10 bg-white hover:border-black/20 hover:bg-black/[0.02]'
              )}
            >
              <p className={cn("text-[15px] font-bold", frequency === freq.value ? "text-brand-900" : "text-foreground")}>{freq.label}</p>
              <p className={cn("mt-1 text-[13px] font-medium text-foreground/50", frequency === freq.value && "text-brand-700/80")}>{freq.description}</p>
            </button>
          ))}
        </div>
        {config?.frequencyHint && (
          <p className="mt-3 text-[13px] font-bold text-brand-600/70">💡 {config.frequencyHint}</p>
        )}
      </div>

      {/* Episode length */}
      <div className="mb-8">
        <label className="mb-3 block text-[15px] font-bold text-foreground">Episode length</label>
        <div className="flex flex-wrap gap-2">
          {DURATIONS.map((dur) => {
            const disabled = maxDurationMinutes !== undefined && dur > maxDurationMinutes
            return (
              <button
                key={dur}
                onClick={() => !disabled && onDurationChange(dur)}
                disabled={disabled}
                title={disabled ? `Your plan supports up to ${maxDurationMinutes} min episodes` : undefined}
                className={cn(
                  'flex-1 rounded-2xl border py-3 text-center text-[15px] font-bold transition-all',
                  disabled
                    ? 'cursor-not-allowed border-black/5 bg-black/[0.02] text-foreground/30'
                    : 'cursor-pointer',
                  !disabled && episodeDurationMinutes === dur
                    ? 'border-brand-300 bg-brand-50/70 text-brand-700 ring-1 ring-brand-300'
                    : !disabled && 'border-black/10 bg-white text-foreground/60 hover:border-black/20 hover:bg-black/[0.02]'
                )}
              >
                {dur} min
              </button>
            )
          })}
        </div>
        {maxDurationMinutes !== undefined && maxDurationMinutes < 30 && (
          <p className="mt-3 text-[13px] font-medium text-foreground/50">
            Upgrade your plan in Billing to unlock longer episodes.
          </p>
        )}
        {config?.durationHint && (
          <p className="mt-3 text-[13px] font-bold text-brand-600/70">💡 {config.durationHint}</p>
        )}
      </div>

      {/* Delivery time */}
      <div className="mb-8">
        <label className="mb-3 block text-[15px] font-bold text-foreground">Delivery time</label>
        <div className="flex items-center gap-3">
          <input
            type="time"
            value={timeValue}
            onChange={handleTimeChange}
            className="rounded-2xl border border-black/10 bg-white px-5 py-3 text-[15px] font-bold text-foreground shadow-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
          />
          <span className="text-[15px] font-medium text-foreground/50">{timezone}</span>
        </div>
        {config?.timeHint && (
          <p className="mt-3 text-[13px] font-bold text-brand-600/70">💡 {config.timeHint}</p>
        )}
      </div>

      {/* Timezone override */}
      <div>
        <label className="mb-2 block text-xs font-bold text-foreground/40 uppercase tracking-wider">Timezone</label>
        <select
          value={timezone}
          onChange={(e) => onTimezoneChange(e.target.value)}
          className="w-full cursor-pointer rounded-2xl border border-black/10 bg-white px-5 py-3 text-[15px] font-medium text-foreground shadow-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
        >
          {Intl.supportedValuesOf('timeZone').map((tz) => (
            <option key={tz} value={tz}>
              {tz.replace(/_/g, ' ')}
            </option>
          ))}
        </select>
      </div>
    </div>
  )
}
